import React, { useState, useEffect, useCallback } from 'react';
import { productApi, PRODUCT_KIND, groupByCategory } from '../api/stockApi';
import { formatRupiah } from '../api/orderApi';
import './Orders.css';
import './Stock.css';

const EMPTY = { name: '', category: '', price: '', resellerPrice: '', cost: '', kind: 'produksi', packOf: '', packSize: '', sortOrder: '' };

const toNumber = (v) => (v === '' || v === null || v === undefined ? undefined : Number(v));

const KIND_WORD = /beli|konsin/i;
const AMOUNT = /\d[\d.,]*/;
const isMoney = (s) => /^(rp\.?\s*)?\d[\d.,]*(\s|$)/i.test(s);
// First amount in the text: "Rp 19.000 konsinyasi" -> 19000
const money = (s) => {
  const m = String(s || '').match(AMOUNT);
  const digits = m ? m[0].replace(/[^\d]/g, '') : '';
  return digits ? Number(digits) : undefined;
};

/**
 * One product per line, columns separated by ";", "|" or tabs (so an Excel table can be pasted as is):
 *   Nama; Kategori; Harga; Jenis; Modal   — Kategori, Jenis and Modal are optional.
 * Jenis "beli jadi" / "konsinyasi" marks bought-in goods, anything else is produksi.
 * Prices may be written as 5000, 5.000 or Rp 5.000. A header row starting with "Nama" is skipped.
 */
function parseImport(text) {
  const rows = [];
  const errors = [];
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line) return;
    const parts = line.split(/[;\t|]/).map((p) => p.trim()).filter(Boolean);
    const [name, ...rest] = parts;
    if (/^nama\b/i.test(name)) return;

    const category = rest.length && !isMoney(rest[0]) && !KIND_WORD.test(rest[0]) ? rest.shift() : '';
    const priceIdx = rest.findIndex(isMoney);
    const price = priceIdx >= 0 ? money(rest[priceIdx]) : undefined;
    if (!name || price === undefined) {
      errors.push(`Baris ${i + 1}: "${line}" — butuh minimal nama dan harga`);
      return;
    }
    // The cost is the next amount after the price, even when it shares a cell with it or with
    // the kind, e.g. "Rp 19.000 konsinyasi Rp 16.500" or "konsinyasi  Rp 2.300"
    const afterPrice = [rest[priceIdx].replace(AMOUNT, ''), ...rest.slice(priceIdx + 1)].join(' ');
    rows.push({
      name,
      category: category || undefined,
      price,
      kind: rest.some((x) => KIND_WORD.test(x)) ? 'beli_jadi' : 'produksi',
      cost: money(afterPrice)
    });
  });
  return { rows, errors };
}

const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingId, setEditingId] = useState(null); // 'new' for the add row
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setProducts(await productApi.list(true));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const startEdit = (p) => {
    setEditingId(p ? p.id : 'new');
    setForm(p
      ? {
        name: p.name,
        category: p.category || '',
        price: String(p.price),
        resellerPrice: p.resellerPrice !== undefined ? String(p.resellerPrice) : '',
        cost: p.cost !== undefined ? String(p.cost) : '',
        kind: p.kind,
        packOf: p.packOf || '',
        packSize: p.packSize ? String(p.packSize) : '',
        sortOrder: String(p.sortOrder)
      }
      : EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      category: form.category,
      price: toNumber(form.price) ?? 0,
      resellerPrice: toNumber(form.resellerPrice),
      cost: toNumber(form.cost),
      kind: form.kind,
      packOf: form.packOf || null,
      packSize: form.packOf ? toNumber(form.packSize) : undefined,
      sortOrder: toNumber(form.sortOrder)
    };
    setSaving(true);
    try {
      if (editingId === 'new') await productApi.create(payload);
      else await productApi.update(editingId, payload);
      setEditingId(null);
      setMessage({ type: 'success', text: 'Produk tersimpan' });
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    if (p.isActive && !window.confirm(`Nonaktifkan ${p.name}? Produk tidak muncul lagi di kiriman dan tutup toko.`)) return;
    try {
      await productApi.update(p.id, { isActive: !p.isActive });
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const preview = parseImport(importText);

  const runImport = async () => {
    if (preview.errors.length || preview.rows.length === 0) return;
    setSaving(true);
    try {
      await productApi.createBatch(preview.rows);
      setMessage({ type: 'success', text: `${preview.rows.length} produk berhasil diimport` });
      setImportText('');
      setShowImport(false);
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const visible = products.filter((p) => showInactive || p.isActive);
  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))];
  const nameOf = (id) => products.find((p) => p.id === id)?.name || '?';
  // A pack is made of a piece product, which cannot itself be a pack
  const pieceOptions = products.filter((p) => !p.packOf && p.id !== editingId);

  const renderForm = () => (
    <form className="stk-product-form" onSubmit={save}>
      <input placeholder="Nama produk" value={form.name} onChange={(e) => set('name', e.target.value)} required autoFocus />
      <input list="product-categories" placeholder="Kategori" value={form.category} onChange={(e) => set('category', e.target.value)} />
      <datalist id="product-categories">
        {categories.map((c) => <option key={c} value={c} />)}
      </datalist>
      <input type="number" inputMode="numeric" min="0" placeholder="Harga jual" value={form.price} onChange={(e) => set('price', e.target.value)} required />
      <select value={form.kind} onChange={(e) => set('kind', e.target.value)}>
        {Object.entries(PRODUCT_KIND).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <input type="number" inputMode="numeric" min="0" placeholder="Harga reseller" value={form.resellerPrice} onChange={(e) => set('resellerPrice', e.target.value)} />
      <input type="number" inputMode="numeric" min="0" placeholder="Modal (opsional)" value={form.cost} onChange={(e) => set('cost', e.target.value)} />
      <input type="number" inputMode="numeric" placeholder="Urutan" value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
      <select value={form.packOf} onChange={(e) => set('packOf', e.target.value)} title="Isi pack, untuk rakit/bongkar di outlet">
        <option value="">Bukan pack</option>
        {pieceOptions.map((p) => <option key={p.id} value={p.id}>Pack dari: {p.name}</option>)}
      </select>
      {form.packOf && (
        <input
          type="number"
          inputMode="numeric"
          min="2"
          placeholder="Isi per pack (pcs)"
          value={form.packSize}
          onChange={(e) => set('packSize', e.target.value)}
          required
        />
      )}
      <div className="ord-actions">
        <button type="button" className="ord-btn ghost" onClick={() => setEditingId(null)} disabled={saving}>Batal</button>
        <button type="submit" className="ord-btn primary" disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</button>
      </div>
    </form>
  );

  return (
    <div className="ord-container">
      <div className="ord-header">
        <h2>📦 Master Produk</h2>
        <div className="ord-actions" style={{ marginTop: 0 }}>
          <button className="ord-btn ghost" onClick={() => setShowImport(!showImport)}>📋 Tempel Daftar</button>
          <button className="ord-btn primary" onClick={() => startEdit(null)}>➕ Produk</button>
        </div>
      </div>
      <p className="ord-line muted">Barang jadi yang dijual di outlet. Harga jual sama untuk semua outlet.</p>

      {message.text && <div className={`ord-alert ${message.type === 'error' ? 'error' : 'success'}`}>{message.text}</div>}

      {showImport && (
        <div className="ord-panel inset">
          <h4>Tempel daftar produk</h4>
          <p className="ord-line muted small">
            Satu produk per baris: <code>Nama; Kategori; Harga; Jenis; Modal</code>. Kategori, jenis dan modal boleh dikosongkan.
            Tulis "beli jadi" atau "konsinyasi" untuk barang yang dibeli dari supplier. Tabel Excel bisa langsung di-copy.
          </p>
          <textarea
            className="stk-import"
            rows="8"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder={'Risoles Kampung; RISOLES GORENG; 2500\nDonat Kentang; DONAT GORENG; 3500; beli jadi; 2800'}
          />
          {preview.errors.map((err) => <div key={err} className="ord-line small neg">{err}</div>)}
          {preview.rows.length > 0 && preview.errors.length === 0 && (
            <div className="ord-line small">
              {preview.rows.length} produk siap diimport:
              {groupByCategory(preview.rows).map((g) => (
                <div key={g.category} className="stk-preview-group">
                  <strong>{g.category}</strong> ({g.items.length}):{' '}
                  {g.items.map((r) => `${r.name} ${formatRupiah(r.price)}${r.kind === 'beli_jadi' ? ' (beli jadi)' : ''}`).join(', ')}
                </div>
              ))}
            </div>
          )}
          <div className="ord-actions">
            <button className="ord-btn ghost" onClick={() => setShowImport(false)}>Batal</button>
            <button
              className="ord-btn primary"
              onClick={runImport}
              disabled={saving || preview.errors.length > 0 || preview.rows.length === 0}
            >
              Import {preview.rows.length || ''} Produk
            </button>
          </div>
        </div>
      )}

      {editingId === 'new' && <div className="ord-panel">{renderForm()}</div>}

      {loading ? (
        <div className="ord-empty">Memuat produk...</div>
      ) : visible.length === 0 ? (
        <div className="ord-empty">Belum ada produk. Klik "Tempel Daftar" untuk memasukkan banyak produk sekaligus.</div>
      ) : (
        <div className="stk-table-wrap">
          <table className="stk-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Produk</th>
                <th>Jenis</th>
                <th className="num">Harga</th>
                <th className="num">Reseller</th>
                <th className="num">Modal</th>
                <th></th>
              </tr>
            </thead>
            {groupByCategory(visible).map((g) => (
              <tbody key={g.category}>
                <tr className="stk-group">
                  <td colSpan="7">{g.category} <span className="muted">({g.items.length})</span></td>
                </tr>
                {g.items.map((p) => (
                  editingId === p.id ? (
                    <tr key={p.id}><td colSpan="7">{renderForm()}</td></tr>
                  ) : (
                    <tr key={p.id} className={p.isActive ? '' : 'dim'}>
                      <td className="muted">{p.sortOrder}</td>
                      <td>
                        {p.name}{!p.isActive && <span className="muted"> (nonaktif)</span>}
                        {p.packOf && <div className="muted small">📦 isi {p.packSize} × {nameOf(p.packOf)}</div>}
                      </td>
                      <td>{PRODUCT_KIND[p.kind]}</td>
                      <td className="num">{formatRupiah(p.price)}</td>
                      <td className="num muted">{p.resellerPrice !== undefined ? formatRupiah(p.resellerPrice) : '-'}</td>
                      <td className="num muted">{p.cost !== undefined ? formatRupiah(p.cost) : '-'}</td>
                      <td className="stk-row-actions">
                        <button className="ord-btn ghost small" onClick={() => startEdit(p)}>✏️</button>
                        <button className="ord-btn ghost small" onClick={() => toggleActive(p)}>
                          {p.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                        </button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}

      <label className="ord-line muted small stk-check">
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Tampilkan produk nonaktif
      </label>
    </div>
  );
};

export default ProductManager;
